import _extends from "@babel/runtime/helpers/extends";

/** @jsx jsx */
import React, { forwardRef, useMemo } from 'react';
import { css, jsx } from '@emotion/core';
import { DN600, N800 } from '@atlaskit/theme/colors';
import GlobalTheme from '@atlaskit/theme/components';
import { borderRadius, fontSize, gridSize } from '@atlaskit/theme/constants';
import { headingSizes } from '@atlaskit/theme/typography';
import { getAppearanceIconStyles } from './internal/appearance-icon';
var spacing = gridSize();
var titleMarginBottom = spacing;
var containerPadding = spacing * 2;
var actionMarginTop = spacing;
var iconWrapperWidth = spacing * 5;
var titleColor = {
  light: "var(--ds-text-highEmphasis, ".concat(N800, ")"),
  dark: "var(--ds-text-highEmphasis, ".concat(DN600, ")")
};
var containerStyles = css({
  display: 'flex',
  padding: "".concat(containerPadding, "px"),
  borderRadius: "".concat(borderRadius(), "px")
});
var contentContainerStyles = css({
  flexGrow: 1
});
var titleStyles = css({
  margin: "0 0 ".concat(titleMarginBottom, "px"),
  fontSize: "".concat(headingSizes.h500.size / fontSize(), "em"),
  fontStyle: 'inherit',
  fontWeight: 600,
  letterSpacing: '-0.006em',
  lineHeight: "".concat(headingSizes.h500.lineHeight / headingSizes.h500.size)
});
var actionsStyles = css({
  display: 'flex',
  marginTop: "".concat(actionMarginTop, "px"),
  paddingLeft: 0,
  listStyle: 'none'
}); // If the icon is not wrapped in a div with a width, and we instead use margin or
// padding, the icon is shrunk by the padding.
// Since the icons will have a consistent size, we can treat them as pre-calculated
// space.

var iconWrapperStyles = css({
  display: 'flex',
  width: "".concat(iconWrapperWidth, "px"),
  margin: '-2px 0',
  flex: '0 0 auto'
});
var SectionMessageWithMode = /*#__PURE__*/forwardRef(function SectionMessage(_ref, ref) {
  var children = _ref.children,
      _ref$appearance = _ref.appearance,
      appearance = _ref$appearance === void 0 ? 'information' : _ref$appearance,
      actions = _ref.actions,
      title = _ref.title,
      icon = _ref.icon,
      testId = _ref.testId,
      mode = _ref.mode;

  var _getAppearanceIconSty = getAppearanceIconStyles(appearance, icon),
      backgroundColor = _getAppearanceIconSty.backgroundColor,
      primaryIconColor = _getAppearanceIconSty.primaryIconColor,
      Icon = _getAppearanceIconSty.Icon;

  var containerStyleWithBackground = useMemo(function () {
    return {
      backgroundColor: backgroundColor
    };
  }, [backgroundColor]);
  var memoizedTitleColor = useMemo(function () {
    return {
      color: titleColor[mode]
    };
  }, [mode]);
  var actionsArray = React.Children.toArray(actions);
  return jsx("section", {
    css: containerStyles,
    style: containerStyleWithBackground,
    "data-testid": testId,
    ref: ref
  }, jsx("div", {
    css: iconWrapperStyles
  }, jsx(Icon, {
    primaryColor: primaryIconColor,
    secondaryColor: backgroundColor
  })), jsx("div", {
    css: contentContainerStyles
  }, title ? jsx("h1", {
    css: titleStyles,
    style: memoizedTitleColor
  }, title) : null, jsx("div", null, children), actionsArray.length > 0 ? jsx("ul", {
    css: actionsStyles
  }, actionsArray) : null));
});
var SectionMessage = /*#__PURE__*/forwardRef(function SectionMessage(props, ref) {
  return jsx(GlobalTheme.Consumer, null, function (_ref2) {
    var mode = _ref2.mode;
    return jsx(SectionMessageWithMode, _extends({}, props, {
      mode: mode,
      ref: ref
    }));
  });
});
SectionMessage.displayName = 'SectionMessage';
export default SectionMessage;